<template>
  <b-modal ref="modal" size="xl" hide-footer scrollable :modal-class="['swl-modal']" body-class="swl-modal-body">
    <template #modal-title>
      <span v-if="item">
        <span class="swl-channel-pill" :style="{ background: channelColors[item.channel] || '#888' }">
          {{ channelLabels[item.channel] || item.channel }}
        </span>
        <span class="ml-2">{{ $t('logDetail') }}</span>
        <span class="ml-2 small text-muted">{{ item.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
      </span>
      <span v-else>{{ $t('logDetail') }}</span>
    </template>

    <div v-if="loading" class="text-center p-5">
      <b-spinner></b-spinner>
    </div>

    <div v-else-if="item">
      <!-- HÀNG META: 1 row gọn -->
      <div class="swl-meta-grid">
        <div class="swl-meta-cell">
          <div class="swl-meta-label">{{ $t('status') }}</div>
          <div>
            <span v-if="item.status === 'success'" class="badge badge-success swl-status-badge">
              {{ item.resStatus }} OK
            </span>
            <span v-else class="badge badge-danger swl-status-badge">
              {{ item.resStatus || 'ERR' }}
            </span>
          </div>
        </div>
        <div class="swl-meta-cell">
          <div class="swl-meta-label">{{ $t('duration') }}</div>
          <div class="swl-meta-val">{{ item.durationMs }}ms</div>
        </div>
        <div class="swl-meta-cell">
          <div class="swl-meta-label">IP</div>
          <div class="swl-meta-val text-monospace">{{ item.ip || '—' }}</div>
        </div>
        <div class="swl-meta-cell">
          <div class="swl-meta-label">{{ $t('sampledFlag') }}</div>
          <div>
            <span v-if="item.sampled" class="badge badge-secondary" :title="$t('sampledHint')">
              <font-awesome-icon icon="random" class="mr-1" />{{ $t('yes') }}
            </span>
            <span v-else class="text-muted">—</span>
          </div>
        </div>
      </div>

      <div class="swl-meta-grid mt-2">
        <div class="swl-meta-cell">
          <div class="swl-meta-label">{{ $t('client') }}</div>
          <div v-if="item.client" class="swl-meta-val">
            {{ item.client.domain }}
            <span v-if="item.client.name" class="text-muted small">({{ item.client.name }})</span>
          </div>
          <div v-else class="text-muted">—</div>
        </div>
        <div class="swl-meta-cell swl-meta-cell--wide">
          <div class="swl-meta-label">{{ $t('account') }}</div>
          <div v-if="item.account" class="swl-meta-val">
            {{ item.account.name }}
            <span class="text-muted small ml-2">{{ accountIdShort(item.account) }}</span>
          </div>
          <div v-else-if="item.unresolved">
            <span class="badge badge-warning">{{ $t('unresolved') }}</span>
          </div>
          <div v-else class="text-muted">—</div>
        </div>
      </div>

      <!-- URL -->
      <div class="swl-section">
        <div class="swl-section__label">URL</div>
        <div class="swl-url-block">
          <span class="badge badge-dark mr-2">{{ item.method }}</span>
          <code>{{ item.url }}</code>
          <button class="btn btn-sm btn-link p-0 ml-2" @click="copy(item.url)" title="Copy">
            <font-awesome-icon icon="copy" />
          </button>
        </div>
      </div>

      <!-- Error -->
      <div v-if="item.errorMessage" class="alert alert-danger mt-3">
        <strong>{{ $t('errorMessage') }}:</strong> {{ item.errorMessage }}
      </div>

      <!-- REQUEST -->
      <div class="swl-section">
        <div class="swl-section__label swl-section__label--big">
          <font-awesome-icon icon="arrow-down" class="mr-2 text-primary" />
          {{ $t('request') }}
        </div>

        <div class="swl-block">
          <div class="swl-block__head">
            <span>Headers</span>
            <button class="btn btn-sm btn-link p-0" @click="copy(JSON.stringify(item.reqHeaders, null, 2))">
              <font-awesome-icon icon="copy" /> Copy
            </button>
          </div>
          <div class="swl-block__body">
            <json-viewer v-if="item.reqHeaders && Object.keys(item.reqHeaders).length" theme="dark-json-viewer"
              :value="item.reqHeaders" :expand-depth="20" :boxed="false" sort></json-viewer>
            <div v-else class="text-muted small">—</div>
          </div>
        </div>

        <div class="swl-block">
          <div class="swl-block__head">
            <span>
              Body
              <span v-if="item.reqBodyTruncated" class="badge badge-warning ml-2">
                <font-awesome-icon icon="cut" /> {{ $t('bodyTruncated') }}
              </span>
            </span>
            <button class="btn btn-sm btn-link p-0" @click="copy(asJson(item.reqBody))">
              <font-awesome-icon icon="copy" /> Copy
            </button>
          </div>
          <div class="swl-block__body">
            <pre v-if="typeof item.reqBody === 'string'" class="swl-pre">{{ item.reqBody }}</pre>
            <json-viewer v-else-if="item.reqBody" theme="dark-json-viewer" :value="item.reqBody" :expand-depth="20"
              :boxed="false" sort></json-viewer>
            <div v-else class="text-muted small">—</div>
          </div>
        </div>
      </div>

      <!-- RESPONSE -->
      <div class="swl-section">
        <div class="swl-section__label swl-section__label--big">
          <font-awesome-icon icon="arrow-up" class="mr-2 text-success" />
          {{ $t('response') }}
        </div>

        <div class="swl-block">
          <div class="swl-block__head">
            <span>
              Body
              <span v-if="item.resBodyTruncated" class="badge badge-warning ml-2">
                <font-awesome-icon icon="cut" /> {{ $t('bodyTruncated') }}
              </span>
            </span>
            <button class="btn btn-sm btn-link p-0" @click="copy(asJson(item.resBody))">
              <font-awesome-icon icon="copy" /> Copy
            </button>
          </div>
          <div class="swl-block__body">
            <pre v-if="typeof item.resBody === 'string'" class="swl-pre">{{ item.resBody }}</pre>
            <json-viewer v-else-if="item.resBody" theme="dark-json-viewer" :value="item.resBody" :expand-depth="20"
              :boxed="false" sort></json-viewer>
            <div v-else class="text-muted small">—</div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import 'vue-json-viewer/style.css';
import JsonViewer from 'vue-json-viewer';

export default {
  components: { JsonViewer },
  props: {
    channelColors: { type: Object, default: () => ({}) },
    channelLabels: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      item: null,
      loading: false
    }
  },
  methods: {
    open(id) {
      this.item = null;
      this.loading = true;
      this.$refs.modal.show();
      this.$service.get('/social-webhook-log/' + id).then(response => {
        this.item = response.data;
      }).catch(e => {
        this.$swal({
          text: (e.response && e.response.data && e.response.data.msg) || 'Load failed',
          icon: 'error'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    accountIdShort(acc) {
      if (!acc) return '';
      const id = acc.facebookPageId || acc.instagramId || acc.whatsappNumberId || acc.oaId || acc.tiktokId || acc.zaloId;
      return id ? `${acc.type}: ${id}` : acc.type;
    },
    asJson(v) {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
    },
    copy(text) {
      if (!text) return;
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {
        // fallback if execCommand fails
      }
      document.body.removeChild(ta);
      this.$bvToast && this.$bvToast.toast('Đã copy', { variant: 'success', autoHideDelay: 1500, noCloseButton: true });
    }
  },
  i18n: {
    messages: {
      en: {
        logDetail: 'Webhook log',
        request: 'Request',
        response: 'Response',
        bodyTruncated: 'Truncated (>100KB)',
        errorMessage: 'Error',
        unresolved: 'Account not mapped',
        sampledFlag: 'Sampled',
        sampledHint: 'This record was selected via random sampling',
        client: 'Client',
        account: 'Account',
        duration: 'Duration',
        status: 'Status'
      },
      vi: {
        logDetail: 'Chi tiết log',
        request: 'Yêu cầu (Request)',
        response: 'Phản hồi (Response)',
        bodyTruncated: 'Đã cắt (>100KB)',
        errorMessage: 'Lỗi',
        unresolved: 'Chưa map account',
        sampledFlag: 'Lấy mẫu',
        sampledHint: 'Bản ghi này được chọn ngẫu nhiên theo tỉ lệ sample',
        client: 'Khách',
        account: 'Tài khoản',
        duration: 'Thời gian',
        status: 'Trạng thái'
      }
    }
  }
}
</script>

<style>
/* JSON viewer global override để dễ đọc + auto-expand */
.swl-modal .dark-json-viewer,
.swl-modal-body .dark-json-viewer {
  background: #f6f8fa !important;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px;
  border-radius: 4px;
  max-height: 600px;
  overflow: auto;
}

.swl-modal .jv-container.jv-light {
  background: transparent !important;
}

.swl-modal .modal-xl {
  max-width: 1280px;
}
</style>

<style scoped>
.swl-channel-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  vertical-align: middle;
}

.swl-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.swl-meta-cell--wide {
  grid-column: span 3;
}

.swl-meta-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.swl-meta-val {
  font-size: 14px;
  color: #222;
}

.swl-status-badge {
  font-size: 13px;
  padding: 4px 8px;
}

.swl-section {
  margin-top: 24px;
}

.swl-section__label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.swl-section__label--big {
  font-size: 14px;
  color: #222;
  text-transform: none;
  font-weight: 600;
  letter-spacing: normal;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8eaed;
}

.swl-url-block {
  padding: 10px;
  background: #f6f8fa;
  border-radius: 4px;
  word-break: break-all;
  font-family: 'Menlo', 'Monaco', monospace;
  font-size: 13px;
}

.swl-block {
  margin-bottom: 16px;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  overflow: hidden;
}

.swl-block__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f6f8fa;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e8eaed;
}

.swl-block__body {
  padding: 12px;
  background: #fff;
}

.swl-pre {
  background: #f6f8fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 600px;
  overflow: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
